<script setup lang="ts">

    import { timeAgo, time } from '../../helpers';

    const { params: { id: colaboratorId } } = useRoute();
    const { locale } = useI18n();
    const localePath = useLocalePath();

    const colaboratorData = ref({} as any);
    const articlesData = ref([] as any);
    const isReady = ref(false);
    const noColaborator = ref(false);

    const loadColaboratorData = async (): Promise<any> => {
        return await $fetch(`/api/strapi/colaborator`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                locale: locale.value,
                colaboratorId
            })
        });
    }

	const { articles, colaboratorLocalized } = await loadColaboratorData();
	const profilePageJsonLd = {
		"@context": "https://schema.org",
		"@type": "ProfilePage",
		"mainEntity": {
			"@type": "Person",
			"name": colaboratorLocalized?.createdBy.firstname,
			"url": "https://thepoliticalroom.com/colaborador/" + colaboratorId,
			"description": colaboratorLocalized?.bio,
			"sameAs": [
				colaboratorLocalized?.website,
				colaboratorLocalized?.facebook,
				colaboratorLocalized?.twitter,
			]
		}
	}

	useHead({
		script: [
			{
				id: 'profilepage-jsonld',
				type: 'application/ld+json',
				innerHTML: JSON.stringify(profilePageJsonLd)
			}
		]
	});

    onMounted(async () => {

        colaboratorData.value = colaboratorLocalized;
        articlesData.value = articles;

        if (colaboratorData.value) {
            isReady.value = true;
        } else {
            noColaborator.value = true
        }
    });


</script>

<template>

    <div v-if="isReady">

        <Head>
            <Title>{{ (locale === 'es' ? 'Colaborador ' : 'Colaborator ') + colaboratorData.createdBy.firstname || 'The Political Room' }}</Title>
            <Meta name="description" :content=" (locale === 'es' ? 'Colaboradores' : 'Colaborators') + 'The Political Room'" />
            <Html :lang="locale" />
        </Head>

        <div class="section-profile">
            <div class="profile-wrap is-cover" :style="{ backgroundImage: `url(${colaboratorData.profile_image ? colaboratorData.profile_image.url : colaboratorData.profile_image_url ? colaboratorData.profile_image_url : useRuntimeConfig().public.AMAZON_S3_URL + '2020/05/WhatsApp-Image-2020-03-28-at-18.36.13.jpeg' }` }">
                <div class="author-image" style="background-image: url(https://thepoliticalroom.com/content/images/2020/05/00-Logo-TPR.jpeg)"></div>
                <h1>{{ colaboratorData.createdBy.firstname + ( colaboratorData.createdBy.lastname ? ' ' + colaboratorData.createdBy.lastname : '' )}}</h1>
                <div class="author-social">
                    <a v-if="colaboratorData.website" :href="colaboratorData.website" aria-label="link Website"><svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.2788278,2.10765437 C14.8256905,-0.449446705 18.9706955,-0.76072067 21.6820313,1.61965243 C24.6552536,4.23057191 24.7681965,8.79131732 22.0208601,11.550601 L17.5906743,15.9986187 C15.4249939,18.1731728 12.0988251,18.8735392 9.35219455,17.5128066 C8.41194479,17.0473502 7.68416888,16.4058931 7.11592484,15.6567992 C6.89780384,15.368798 6.92109832,14.9629782 7.17592577,14.7069772 L8.21005933,13.6691549 C8.52347591,13.3542446 9.02677778,13.4276994 9.28301701,13.7913372 C9.46019621,14.0422473 9.66702292,14.2735209 9.90490894,14.482249 C11.4797567,15.8669818 13.9002645,15.6575264 15.3805226,14.1709751 L20.005535,9.52659297 C21.6241481,7.90113196 21.5281466,5.19421211 19.7154128,3.69747888 C18.1349179,2.39274642 15.7864112,2.63347465 14.3393301,4.08584406 L13.6376723,4.79057414 C13.3595503,5.06984797 12.9077787,5.06984797 12.6296567,4.79057414 L11.6223471,3.77893375 C11.3442252,3.49965991 11.3442252,3.0465672 11.6223471,2.76729336 L12.2788278,2.10765437 Z M15.2534618,9.66113896 C14.9703987,9.94477645 14.5228624,9.92804911 14.2362697,9.64877528 C14.0379137,9.45531996 13.8169691,9.28077381 13.5741419,9.12804593 C12.0077648,8.14258747 9.94514485,8.49822525 8.63853651,9.81023046 L4.11870225,14.3491576 C2.67162116,15.801527 2.43232336,18.1600819 3.73116688,19.7469973 C5.22201335,21.5673681 7.91781954,21.6640958 9.53713857,20.0386348 L10.3623277,19.2095406 C10.6411556,18.9302667 11.0922213,18.9302667 11.3703433,19.2095406 L12.3776529,20.221181 C12.6557748,20.5004548 12.6557748,20.9535475 12.3776529,21.2328214 L11.7211722,21.8924603 C9.17430946,24.4495614 5.0293045,24.7608354 2.31796866,22.379735 C-0.655253565,19.7688155 -0.768196479,15.2087974 1.97913991,12.4495137 L6.39309019,8.01749606 C7.98558528,6.41821697 10.2119725,5.55712264 12.4489481,5.82257824 C14.095797,6.01748811 15.324757,6.70258174 16.2607714,7.63567636 C16.5403052,7.9149502 16.5374816,8.37167929 16.2586538,8.6516804 L15.2534618,9.66113896 Z"></path></svg></a>
                    <a v-if="colaboratorData.facebook" :href="'https://facebook.com/' + colaboratorData.facebook" aria-label="link Facebook"><svg class="global-svg" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24h11.494v-9.294H9.689v-3.621h3.129V8.41c0-3.099 1.894-4.785 4.659-4.785 1.325 0 2.464.097 2.796.141v3.24h-1.921c-1.5 0-1.792.721-1.792 1.771v2.311h3.584l-.465 3.63H16.56V24h6.115c.733 0 1.325-.592 1.325-1.324V1.324C24 .593 23.408 0 22.676 0"></path></svg></a>
                    <a v-if="colaboratorData.twitter" :href="'https://twitter.com/' + colaboratorData.twitter" aria-label="link Twitter"><svg class="global-svg" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.954 4.569c-.885.389-1.83.654-2.825.775 1.014-.611 1.794-1.574 2.163-2.723-.951.555-2.005.959-3.127 1.184-.896-.959-2.173-1.559-3.591-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124C7.691 8.094 4.066 6.13 1.64 3.161c-.427.722-.666 1.561-.666 2.475 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.228-.616v.061c0 2.385 1.693 4.374 3.946 4.827-.413.111-.849.171-1.296.171-.314 0-.615-.03-.916-.086.631 1.953 2.445 3.377 4.604 3.417-1.68 1.319-3.809 2.105-6.102 2.105-.39 0-.779-.023-1.17-.067 2.189 1.394 4.768 2.209 7.557 2.209 9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63.961-.689 1.8-1.56 2.46-2.548l-.047-.02z"></path></svg></a>
                </div>
                <h2 v-if="colaboratorData.bio">{{ colaboratorData.bio }}</h2>
            </div>
        </div>
        <div id="loop" class="section-loop wrap">
            <div class="items-wrap flex post is-image">
                <div v-for="article in articlesData" :key="article.id" class="item-wrap flex post featured is-image">
                    <article>
                        <NuxtLink :to="localePath({ name: 'blog-slug', params: { slug: article.slug } })" class="item-link-overlay" :aria-label="article.title"></NuxtLink>

                        <div class="item-image" :style="{ backgroundImage: `url(${article.cover && article.cover[0].url})` }"></div>

                        <NuxtLink :to="localePath({ name: 'blog-slug', params: { slug: article.slug } })" class="white">
                            <h2> {{ article.title }} </h2>
                        </NuxtLink>

                        <div class="item-meta white is-primary-tag is-members-label">
                            <span>por </span>
                            <NuxtLink :to="localePath({ name: 'colaborate-id', params: { id: article.author.id } })" class="author-name white">
                                {{ article.author.firstname }}
                            </NuxtLink>
                            <div v-if="article.other_authors && article.other_authors.length > 0" style="display: inline">
                                /
                                <NuxtLink :to="localePath({ name: 'colaborate-id', params: { id: article.other_authors[0].id } })" class="author-name white">
                                    {{ article.other_authors[0].firstname || 'otro autor' }}
                                </NuxtLink>
                            </div>
                            <br>
                            <time>{{ time(article.publishedAt) }}</time>
                        </div>
                        <a class="primary-tag global-tag white" href="#">{{ article.article_categories && article.article_categories[0] && article.article_categories[0].name }}</a>
                        <a class="secondary-tag global-tag white" href="#" v-if="article && article.premium">PREMIUM</a>
                    </article>
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="noColaborator">
        <div style="display: flex;justify-content: center;align-items: center">
            <div style="height: 30rem;margin: 4rem 1rem">

                <div class="members-cta members-cta-white">
                    <h2 class="members-cta-title members-cta-white">Este colaborador no tiene artículos publicados.</h2>

                </div>
            </div>
        </div>
    </div>
    <div  v-else class="spinner">
        <Spinner />
    </div>

</template>
